import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { deleteAccessToken, removeCookie } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { handleAfterLogin } from '@backrs_ui/grow-ui/src/utils/loginHelper';

import { Box, Button, CircularProgress, SxProps, Theme, Typography } from '@mui/material';

const OAUTH_CALLBACK_STRINGS = appStrings.modules.oauthCallback;

type ErrorStatus = 'needs_registration' | 'service_unavailable' | 'error';
type Status = 'loading' | ErrorStatus;

const ERROR_MESSAGES: Record<ErrorStatus, { title: string; subtitle: string }> = {
  needs_registration: OAUTH_CALLBACK_STRINGS.needsRegistration,
  service_unavailable: OAUTH_CALLBACK_STRINGS.serviceUnavailable,
  error: OAUTH_CALLBACK_STRINGS.genericError,
};

interface ErrorResponse {
  code?: string;
  message?: string;
}

interface SuccessResponse {
  token?: string;
}

interface OAuthExchangeResult {
  status: number;
  body: ErrorResponse | SuccessResponse;
}

/*
 * Dedup the token exchange across StrictMode remounts and double-fires.
 * Stytch tokens are single-use; a second call with the same token would 404.
 * Module scope (not useRef) is required — refs don't survive unmount/remount.
 * Rejections evict so a future retry re-fetches; successes stay until reload.
 */
const oAuthExchanges = new Map<string, Promise<OAuthExchangeResult>>();

const exchangeOAuthToken = (token: string): Promise<OAuthExchangeResult> => {
  const cached = oAuthExchanges.get(token);

  if (cached) return cached;

  const pending = (async () => {
    const response = await fetch(`${ENVIRONMENT_URL}/oauth/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    const body = (await response.json().catch((err) => {
      console.warn('[OAuthCallbackPage] failed to parse /oauth/authenticate body', err);

      return {};
    })) as ErrorResponse | SuccessResponse;

    return { status: response.status, body };
  })();

  pending.catch(() => oAuthExchanges.delete(token));
  oAuthExchanges.set(token, pending);

  return pending;
};

export const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let ignore = false;

    removeCookie();
    deleteAccessToken('backrs_ui_token');

    const stytchToken = searchParams.get('token');

    if (!stytchToken) {
      setStatus('error');

      return;
    }

    const safeSetStatus = (next: Status) => {
      if (!ignore) {
        setStatus(next);
      }
    };

    /*
     * handleAfterLogin writes cookie + localStorage token before /users/auto-login,
     * so a failure there must undo them or a partial session survives into useBootstrap.
     */
    const handleAuthFollowUpError = (message?: string) => {
      console.error('[OAuthCallbackPage] post-token user fetch failed', { message });
      removeCookie();
      deleteAccessToken('backrs_ui_token');
      safeSetStatus('error');
    };

    const authenticate = async () => {
      let result: OAuthExchangeResult;

      try {
        result = await exchangeOAuthToken(stytchToken);
      } catch (err) {
        console.error('[OAuthCallbackPage] /oauth/authenticate fetch failed', err);
        safeSetStatus('error');

        return;
      }

      if (result.status === 404) {
        const body = result.body as ErrorResponse;

        safeSetStatus(body.code === 'NEEDS_REGISTRATION' ? 'needs_registration' : 'error');

        return;
      }

      if (result.status === 503) {
        safeSetStatus('service_unavailable');

        return;
      }

      if (result.status < 200 || result.status >= 300) {
        console.error('[OAuthCallbackPage] /oauth/authenticate returned non-2xx', {
          status: result.status,
        });
        safeSetStatus('error');

        return;
      }

      const data = result.body as SuccessResponse;

      if (!data.token) {
        console.error('[OAuthCallbackPage] /oauth/authenticate 2xx but no token in body', {
          status: result.status,
        });
        safeSetStatus('error');

        return;
      }

      /*
       * Don't run handleAfterLogin's side effects if this effect was torn down;
       * the cookie/token it writes would survive past the next mount.
       */
      if (ignore) {
        return;
      }

      await handleAfterLogin({
        access_token: data.token,
        dispatch,
        navigate,
        setErrorMessage: handleAuthFollowUpError,
      });
    };

    void authenticate();

    return () => {
      ignore = true;
    };
  }, [searchParams, dispatch, navigate]);

  if (status === 'loading') {
    return (
      <Box sx={containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="text-md-bold">{OAUTH_CALLBACK_STRINGS.loading}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const messages = ERROR_MESSAGES[status];

  return (
    <Box sx={containerSx}>
      <Box sx={contentWrapperSx}>
        <Box sx={messageBlockSx}>
          <Typography variant="text-lg-semibold">{messages.title}</Typography>
          <Typography variant="text-md-medium">{messages.subtitle}</Typography>
        </Box>
        <Box sx={buttonRowSx}>
          <Button
            id="oauth-callback-back-to-login-button"
            data-ph-capture-attribute-name="oauth-callback-back-to-login-button"
            hierarchy="Primary"
            size="lg"
            onClick={() => navigate(APP_ROUTE.LOGIN)}
          >
            {OAUTH_CALLBACK_STRINGS.backToLogin}
          </Button>
          {status === 'needs_registration' && (
            <Button
              id="oauth-callback-contact-support-button"
              data-ph-capture-attribute-name="oauth-callback-contact-support-button"
              hierarchy="Secondary gray"
              size="lg"
              onClick={() => window.open('mailto:support@backrs.com', '_blank')}
            >
              {OAUTH_CALLBACK_STRINGS.contactSupport}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100vh',
  width: '100vw',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xl'],
});

const contentWrapperSx: SxProps<Theme> = {
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const messageBlockSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-3xl'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const buttonRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});
